import React, { useCallback, useEffect, useRef, useState } from 'react';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import styles from './FinancialMetrics.module.scss';
import { fetchFinancialMetrics } from '../../../../../../store/dashboard/financials/financialsApiHelpers';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { ctaPurpleRight, infoToolTip } from '../../../../../../assets/hostedassets';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { updateQueryParams } from '../../../../../../utils/searchParamUtils';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_FINANCIAL_METRICS } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { useAppSelector } from '../../../../../../app/hooks';
import { ACCESS_TIERS } from '../../../../../../store/investor/constants';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import FinancialLoaderComponent from '../../../../../../common/FinancialLoader/FinancialLoaderComponent';
import clsx from 'clsx';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { reportSectionsToShow } from '../../../../../AicaPDFReport/AicaPDFReport';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import { AUDITED_TYPE_CONSOLIDATED, AUDITED_TYPE_STANDALONE } from '../../../../../../utils/constants/commonConstants';
import { getStartDateEndDate } from '../../../../../../utils/dateUtils';
import { DATA_FETCH_STATUS } from '../../../../../../store/dashboard/constants';
import AskAicaQuestionBadge from '../../../../../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';
import EditableDropdown from '../../../../../../common/EditableDropdown/EditableDropdown';
import AuditorCommentsPill from '../../../../../../common/AuditorCommentsPill/AuditorCommentsPill';
import { useAuditedType } from '../../../AuditedFinancialsVisualisation/useAuditedType';
import AIText from '../../../../../../common/AIText/AIText';
import { HEADER_TYPE } from '../../../../../../store/dashboard/auditedFinancials/constants';

const activityRatios = [
    'daysPayablesOutStanding',
    'daysInventoryOutStanding',
    'daysSalesOutStanding',
    'CashConversionCycleDays',
    'SalesByNetFixedAssets',
];
const liquidityRatios = ['CurrentRatio', 'QuickRatio'];
const solvencyRatios = ['DebtToEquity', 'TOLByTNW', 'DSCR', 'ISCR'];
const otherMetrics = [
    'RevenueGrowthPercentage',
    'GrossProfitMargin',
    'EbitaMargin',
    'ShareholdersFund',
    'TangibleNetWorth',
    'NetWorkingCapital',
    'ReceivablesPayablesGap',
    'DrawingPower',
];
const overviewMetrics = [
    'Revenue',
    'RevenueGrowthPercentage',
    'EbitaMargin',
    'PAT',
    'CashAndCashEquivalent',
    'TotalCurrentAssets',
    'TotalCurrentLiabilities',
    'NetWorkingCapital',
    'LongTermDebt',
    'ShortTermDebt',
    'TangibleNetWorth',
];
const AUDITED_STATUS = {
    PROCESSING: 'Processing',
    COMPLETE: 'Processed',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
};
const UW_FETCH_FINANCIAL_METRICS = 'UW_FETCH_FINANCIAL_METRICS';
const UW_FETCH_FINANCIALS_RAW_BY_CLASSIFICATION = 'UW_FETCH_FINANCIALS_RAW_BY_CLASSIFICATION';
const AICA_GET_REPORT_STATUS_FROM_ORG_MASTER = 'AICA_GET_REPORT_STATUS_FROM_ORG_MASTER';
const BORROWINGS_CURRENT = 'borrowingsCurrent';
const BORROWINGS_NON_CURRENT = 'borrowingsNonCurrent';
const OVERVIEW = 'overview';
const DASHBOARD = 'dashboard';

const activityRatiosStart = 0;
const liquidityRatiosStart = activityRatios?.length;
const solvencyRatiosStart = liquidityRatiosStart + liquidityRatios?.length;
const otherMetricsStart = solvencyRatiosStart + solvencyRatios?.length;

function FinancialMetricsHeaderCell({ header, isPDFReport }: { header: any; isPDFReport: boolean }) {
    if (!isPDFReport && header?.type === HEADER_TYPE.PROVISIONAL_FINANCIALS) {
        return (
            <AIText
                text={(header?.name ?? '') + '*'}
                animate={false}
                style={{ display: 'block', width: '100%', textAlign: 'inherit', whiteSpace: 'nowrap' }}
            />
        );
    }
    return <>{header?.name ?? ''}</>;
}

function FinancialMetrics({
    screen = '',
    assesseeOrgId = '',
    onClose = () => {},
    isPDFReport = false,
    pdfReportSection = '',
    mcaDocApiData = {},
}: {
    screen?: string;
    assesseeOrgId?: string;
    onClose?: () => void;
    isPDFReport?: boolean;
    pdfReportSection?: string;
    mcaDocApiData?: any;
}) {
    const location = useLocation();
    const params = useParams();
    const user = useAppSelector((state) => state.user.userData);
    const [fetching, setFetching] = useState(false);
    const [financialMetricsData, setFinancialMetricsData] = useState<any>(null);
    const [noDataAvailable, setNoDataAvailable] = useState(true);
    const [auditedFinancialsStatus, setAuditedFinancialsStatus] = useState('');
    const [loaderMessage, setLoaderMessage] = useState<string>('');
    const [searchParams, setSearchParams] = useSearchParams();
    const [isProcessing, setIsProcessing] = useState(false);
    const rowDataExists = !!financialMetricsData?.rows?.length;
    const isMcaCalled = mcaDocApiData?.isFetching === true && mcaDocApiData?.reportStatus === DATA_FETCH_STATUS.PROCESSING;

    const apiPollingRef: any = useRef(null);
    const noDataPollCountRef = useRef(0);
    // Tracks the current nature for polling — updated on user change and initial load.
    const currentNatureRef = useRef<string | undefined>(undefined);

    const fetchData = useCallback(
        (nature?: string) => {
            setFetching(true);
            const { startDate } = getStartDateEndDate();
            fetchFinancialMetrics(
                {
                    orgId: assesseeOrgId,
                    startDate,
                    endDate: null,
                    frequency: 'Yearly',
                    metricsList:
                        screen === 'overview'
                            ? [...overviewMetrics]
                            : [...activityRatios, ...liquidityRatios, ...solvencyRatios, ...otherMetrics],
                    round: true,
                    nature,
                    ...(!isPDFReport && { type: 'auditedMissingProvisionals' }),
                },
                {
                    onSuccess: (data) => {
                        transformAndUpdateData({ ...data });
                    },
                    onError: () => {
                        setFetching(false);
                    },
                },
            );
        },
        [assesseeOrgId, screen, isPDFReport],
    );

    // Called by useAuditedType when user explicitly changes the dropdown
    const onConsolidationChange = useCallback(
        (nature: string) => {
            currentNatureRef.current = nature;
            stopPolling();
            fetchData(nature);
        },
        [fetchData],
    );

    const {
        auditedType,
        latestConsolidatedFY,
        latestStandaloneFY,
        auditedTypeOptionsObject,
        handleConsolidationChange,
        setAuditedTypeFromYears,
    } = useAuditedType(onConsolidationChange, assesseeOrgId);

    useEffect(() => {
        if (isPDFReport && financialMetricsData && Array.isArray(financialMetricsData.headers)) {
            const headersLength = financialMetricsData.headers.length;
            if (headersLength > 1) {
                const updatedHeaders = [
                    financialMetricsData.headers[0],
                    ...(headersLength > 3 ? [financialMetricsData.headers[headersLength - 3]] : []),
                    ...(headersLength > 2 ? [financialMetricsData.headers[headersLength - 2]] : []),
                    financialMetricsData.headers[headersLength - 1],
                ];

                if (JSON.stringify(financialMetricsData.headers) !== JSON.stringify(updatedHeaders)) {
                    setFinancialMetricsData((existing) => ({
                        ...existing,
                        headers: updatedHeaders,
                    }));
                }
            }
        }
    }, [isPDFReport, financialMetricsData]);

    useEffect(() => {
        if (assesseeOrgId && screen) {
            fetchData(undefined);
        }
        return () => {
            stopPolling();
        };
    }, [assesseeOrgId, screen]);

    useEffect(() => {
        if (auditedFinancialsStatus === AUDITED_STATUS.PROCESSING) setIsProcessing(true);
        else setIsProcessing(false);
    }, [auditedFinancialsStatus, noDataAvailable]);

    const startPolling = () => {
        if (!apiPollingRef.current) {
            apiPollingRef.current = setInterval(() => {
                fetchData(currentNatureRef.current);
            }, 10000);
        }
    };

    const stopPolling = () => {
        if (apiPollingRef.current) {
            clearInterval(apiPollingRef.current);
            apiPollingRef.current = null;
        }
    };

    const transformAndUpdateData = (data) => {
        let uwFinancialMetrics = data?.[UW_FETCH_FINANCIAL_METRICS]?.data?.data;
        let uwRawByClassification = data?.[UW_FETCH_FINANCIALS_RAW_BY_CLASSIFICATION]?.data;
        let auditedTaskStatus = data?.[AICA_GET_REPORT_STATUS_FROM_ORG_MASTER]?.reportStatus?.auditedFinancialsStatus;

        const consolidatedFY = uwFinancialMetrics?.latestConsolidatedFY ?? null;
        const standaloneFY = uwFinancialMetrics?.latestStandaloneFY ?? null;

        setAuditedTypeFromYears(consolidatedFY, standaloneFY);

        if (!auditedTaskStatus) setAuditedFinancialsStatus(AUDITED_STATUS.COMPLETE);

        let borrowingsCurrent = uwRawByClassification?.rows?.find((row_data) => row_data?.parent === BORROWINGS_CURRENT);
        let borrowingsNonCurrent = uwRawByClassification?.rows?.find((row_data) => row_data?.parent === BORROWINGS_NON_CURRENT);

        let noDataAvailable = true;
        let updatedRows =
            uwFinancialMetrics?.rows?.map((row) => {
                let rowData = {
                    ...row,
                    ...(row?.metricName === 'Long Term Debt' && borrowingsNonCurrent),
                    ...(row?.metricName === 'Short Term Debt' && borrowingsCurrent),
                };
                uwFinancialMetrics?.headers?.forEach((header, headerIndex) => {
                    if (noDataAvailable && headerIndex > 0 && (rowData?.[header.key] || rowData?.[header.key] === 0)) {
                        noDataAvailable = false;
                    }
                });
                return rowData;
            }) || [];
        uwFinancialMetrics.rows = updatedRows;
        if (auditedTaskStatus === AUDITED_STATUS.COMPLETE) {
            if (noDataAvailable && !noDataPollCountRef.current) {
                startPolling();
                noDataPollCountRef.current = 1;
            } else {
                setAuditedFinancialsStatus(AUDITED_STATUS.COMPLETE);
                stopPolling();
            }
        } else if (auditedTaskStatus === AUDITED_STATUS.PROCESSING) {
            setAuditedFinancialsStatus(AUDITED_STATUS.PROCESSING);
            startPolling();
        } else {
            setAuditedFinancialsStatus(AUDITED_STATUS.COMPLETE);
            stopPolling();
        }

        setNoDataAvailable(noDataAvailable);
        setFinancialMetricsData(uwFinancialMetrics);
        setFetching(false);
    };

    const getSeparatorRowText = (rowIndex) => {
        switch (rowIndex) {
            case activityRatiosStart:
                return 'Activity Ratios';
            case liquidityRatiosStart:
                return 'Liquidity Ratios';
            case solvencyRatiosStart:
                return 'Solvency Ratios';
            case otherMetricsStart:
                return 'Other Derived Metrics';
            default:
                return '';
        }
    };

    const getSeparatorRow = (rowIndex, screenName) => {
        if (screenName === OVERVIEW) return null;
        if ([activityRatiosStart, liquidityRatiosStart, solvencyRatiosStart, otherMetricsStart]?.includes(rowIndex))
            return <div className={styles.SeparatorRow}>{getSeparatorRowText(rowIndex)}</div>;
        else return null;
    };

    const getRowText = (value, unit = '', roundOffRequired = false) => {
        if (typeof value === 'number') {
            if (unit === '%' || roundOffRequired) {
                return parseFloat(value.toFixed(1))?.toLocaleString('en-IN') + unit;
            }
            return parseFloat(value.toFixed(2))?.toLocaleString('en-IN') + unit;
        } else return value ?? '-';
    };

    const showFinancialSummary = () => {
        Mixpanel.track(AICA_FINANCIAL_METRICS, {
            role: user.accountType,
            investorOrgId: user.investorOrgId,
            companyOrgId: params.id,
            AICASpecificSection: 'Overview',
            AICASection: ACCESS_TIERS.SCAN,
        });
        updateQueryParams(searchParams, setSearchParams, { showFinancialMetrics: true }, location);
    };

    const getToolTipContent = (row) => {
        return (
            <div>
                <div>
                    {row?.definitionText && (
                        <>
                            <span className={styles.Bold}>Definition: </span>
                            {row?.definitionText}
                        </>
                    )}
                </div>
                <br />
                <div>
                    {row?.calculationText && (
                        <>
                            <span className={styles.Bold}>Calculation: </span>
                            {row?.calculationText}
                        </>
                    )}
                </div>
                <br />
                <div>
                    {row?.note && (
                        <>
                            <span className={styles.Bold}>Note: </span>
                            {row?.note}
                        </>
                    )}
                </div>
            </div>
        );
    };

    useEffect(() => {
        showLoaderMessage(noDataAvailable);
    }, [isMcaCalled, noDataAvailable]);

    function showLoaderMessage(noDataAvailable) {
        if (isMcaCalled) setLoaderMessage('Fetching latest data from MCA (takes upto 24 hours)');
        else if (noDataAvailable) setLoaderMessage('No Data Available');
        else setLoaderMessage('');
    }

    const consolidatedDropdown = (id: string) => (
        <div className={styles.ConsolidatedDropdown}>
            <EditableDropdown
                id={id}
                optionStyle={{ fontSize: '14px' }}
                value={auditedType || ''}
                options={[AUDITED_TYPE_CONSOLIDATED, AUDITED_TYPE_STANDALONE]}
                optionsObject={auditedTypeOptionsObject}
                setOutput={handleConsolidationChange}
                removeInput={true}
                placeHolder="NA"
                disabled={latestConsolidatedFY === null && latestStandaloneFY === null}
            />
        </div>
    );

    const getTableComponent = (screenName = '') => {
        if (!financialMetricsData?.headers?.length) {
            return <div className={styles.Nodata}>Data not available for last three financial years.</div>;
        }

        // minmax(0,...) pins each track's floor to 0 so every row (each its own grid) resolves
        // to identical column widths regardless of content — otherwise the first column's width
        // varies per row and the left border zigzags.
        const repeatCount = Math.max(0, financialMetricsData.headers.length - 1);
        const gridTemplateColumns = `minmax(0, 2fr) repeat(${repeatCount}, minmax(0, 1fr))`;
        return (
            <>
                <div className={styles.tableContainer}>
                    <div
                        className={clsx(styles.Table, {
                            [styles.overviewScreen]: screenName === OVERVIEW,
                        })}
                    >
                        <div className={styles.TableHeadRow} style={{ gridTemplateColumns }}>
                            {financialMetricsData?.headers?.map((header, i) => (
                                <div key={i} className={styles.TableHeadRowItem}>
                                    <FinancialMetricsHeaderCell header={header} isPDFReport={isPDFReport} />
                                </div>
                            ))}
                        </div>

                        {fetching ? null : rowDataExists && !noDataAvailable ? (
                            financialMetricsData?.rows?.map((row, i) => (
                                <React.Fragment key={i}>
                                    {getSeparatorRow(i, screenName)}
                                    <div className={styles.TableBodyRow} style={{ gridTemplateColumns }}>
                                        {financialMetricsData?.headers?.map((header, j) => (
                                            <div
                                                className={clsx(
                                                    {
                                                        [styles.sectionEnd]: (i == 3 || i == 7) && screenName === OVERVIEW,
                                                        [styles.audited]:
                                                            !isPDFReport && j !== 0 && header.type === HEADER_TYPE.AUDITED_FINANCIALS,
                                                        [styles.provisional]:
                                                            !isPDFReport && j !== 0 && header.type === HEADER_TYPE.PROVISIONAL_FINANCIALS,
                                                    },
                                                    styles.TableBodyRowItem,
                                                )}
                                                key={j}
                                            >
                                                <div className={styles.rowText}>
                                                    {getRowText(row?.[header.key], row?.unit, row?.roundOffRequired)}
                                                </div>
                                                {j === 0 && !isPDFReport && (row?.definitionText || row?.calculationText) && (
                                                    <Tooltip content={getToolTipContent(row)} direction="right">
                                                        <img src={infoToolTip} height={'14px'} />
                                                    </Tooltip>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </React.Fragment>
                            ))
                        ) : (
                            <div className={styles.Nodata}>Data not available for last three financial years.</div>
                        )}
                    </div>
                </div>
                {rowDataExists &&
                    !noDataAvailable &&
                    !isPDFReport &&
                    financialMetricsData?.headers?.some((h) => h?.type === HEADER_TYPE.PROVISIONAL_FINANCIALS) && (
                        <div className={styles.provisionalNote}>*Note: Provisional metrics are updated basis unaudited data.</div>
                    )}
            </>
        );
    };

    const ctaElement = (
        <>
            <div className={styles.Cta} onClick={showFinancialSummary}>
                <img src={ctaPurpleRight} alt="" />
                Financial Ratios
            </div>
            <AuditorCommentsPill orgId={assesseeOrgId} variant="solid" />
        </>
    );

    const renderComponent = () => (
        <div className={styles.financialMetrics}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.heading}>Financial Summary</h2>
                    {!isPDFReport && !noDataAvailable && consolidatedDropdown('FMConsolidatedSelectorOverview')}
                    {!isMcaCalled && loaderMessage && <div className={styles.statusMessage}>{loaderMessage}</div>}
                </div>
                <div className={styles.headerRight}>
                    {isMcaCalled && !isPDFReport && (
                        <div className={styles.loaderMessage}>
                            <LoadingSpinner color="#6021B3" />
                            {loaderMessage}
                        </div>
                    )}
                    {!noDataAvailable && !isPDFReport && ctaElement}
                </div>
            </div>

            {!noDataAvailable && (
                <div className={styles.content}>
                    <div className={styles.contentBody}>{getTableComponent(screen)}</div>
                </div>
            )}
        </div>
    );

    const render =
        screen === DASHBOARD ? (
            isPDFReport ? (
                <>{getTableComponent(screen)}</>
            ) : (
                <SideDrawer onClose={onClose} width="70%">
                    <SideDrawer.Header>
                        <div className={clsx({ [styles.dashboard]: screen === DASHBOARD }, styles.Header)}>
                            <div className={styles.HeaderLeft}>
                                <span>
                                    Financial Metrics{' '}
                                    {!financialMetricsData?.headers?.some((h) => h?.type === HEADER_TYPE.PROVISIONAL_FINANCIALS) && (
                                        <span className={styles.Sub}>| From Audited Financials</span>
                                    )}
                                </span>
                                {!noDataAvailable && consolidatedDropdown('FMConsolidatedSelector')}
                            </div>
                            {isMcaCalled && (
                                <span className={styles.LoaderMessage}>
                                    <LoadingSpinner color="#6021B3" />
                                    Fetching latest data from MCA (takes upto 24 hours)
                                </span>
                            )}
                        </div>
                    </SideDrawer.Header>
                    <div className={styles.FinancialMetrics}>{getTableComponent(screen)}</div>
                </SideDrawer>
            )
        ) : (
            renderComponent()
        );

    if (isPDFReport)
        return !reportSectionsToShow.includes(pdfReportSection) || noDataAvailable ? (
            <></>
        ) : (
            <A4Page sectionName={pdfReportSection}>
                {screen === DASHBOARD ? (
                    <div className={styles.PDFCard}>
                        <div className={styles.Top}>
                            <div className={styles.Title}>{pdfReportSection}</div>
                        </div>
                        <div className={styles.Body}>{render}</div>
                    </div>
                ) : (
                    render
                )}
            </A4Page>
        );
    else return render;
}

export default FinancialMetrics;
